import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ZoomInOutlined, ZoomOutOutlined, ExpandAltOutlined } from '@ant-design/icons';
import { computeLayout, NODE_W, NODE_H } from 'pages/liftlogic/v2/Components/mapLayout';
import { typeStyle, deriveEstado } from 'pages/liftlogic/v2/Components/componentTypeStyle';
import { healthStyle } from 'pages/liftlogic/v2/Components/healthStyle';
import 'pages/liftlogic/v2/Components/componentTree.css';

const STATUS_COLOR = { ok: '#2e6b3a', signal: '#b8861a', warn: '#c5561d', err: '#9b2a1e' };

function ComponentMapView({
  roots,
  selectedId,
  onSelect,
  connector = 'curvo',
  fullscreen = false,
  healthMap = {},
}) {
  const stageRef = useRef(null);
  const drag = useRef(null);
  const [openMap, setOpenMap] = useState(() => {
    const s = {};
    (function w(list) {
      (list || []).forEach((n) => {
        s[n.id] = true;
        w(n.children);
      });
    })(roots);
    return s;
  });
  const [view, setView] = useState({ x: 40, y: 40, k: 1 });

  const isOpen = useCallback((id) => openMap[id] !== false, [openMap]);
  const { pos, edges } = useMemo(() => computeLayout(roots, isOpen), [roots, isOpen]);

  const bounds = useMemo(() => {
    const ps = Object.values(pos);
    if (!ps.length) return { minX: 0, minY: 0, w: NODE_W, h: NODE_H };
    const minX = Math.min(...ps.map((p) => p.x));
    const minY = Math.min(...ps.map((p) => p.y));
    const maxX = Math.max(...ps.map((p) => p.x)) + NODE_W;
    const maxY = Math.max(...ps.map((p) => p.y)) + NODE_H;
    return { minX, minY, w: maxX - minX, h: maxY - minY };
  }, [pos]);

  const fit = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    const W = el.clientWidth;
    const H = el.clientHeight;
    const pad = 48;
    if (!W || !H) return;
    const kw = (W - pad * 2) / bounds.w;
    const k = Math.max(0.4, Math.min(kw, (H - pad * 2) / bounds.h, 1.1));
    setView({ k, x: (W - bounds.w * k) / 2 - bounds.minX * k, y: pad - bounds.minY * k });
  }, [bounds]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth && el.clientHeight) fit();
    });
    ro.observe(el);
    const raf = requestAnimationFrame(() => fit());
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [fit, fullscreen]);

  const onWheel = (e) => {
    e.preventDefault();
    const el = stageRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const mx = e.clientX - r.left;
    const my = e.clientY - r.top;
    const factor = Math.exp(-e.deltaY * 0.0014);
    setView((v) => {
      const nk = Math.min(2.2, Math.max(0.22, v.k * factor));
      const s = nk / v.k;
      return { k: nk, x: mx - (mx - v.x) * s, y: my - (my - v.y) * s };
    });
  };
  const zoomBtn = (dir) => {
    const el = stageRef.current;
    if (!el) return;
    const W = el.clientWidth / 2;
    const H = el.clientHeight / 2;
    setView((v) => {
      const nk = Math.min(2.2, Math.max(0.22, v.k * (dir > 0 ? 1.2 : 1 / 1.2)));
      const s = nk / v.k;
      return { k: nk, x: W - (W - v.x) * s, y: H - (H - v.y) * s };
    });
  };
  const onPointerDown = (e) => {
    if (e.target.closest('.cmp-mnode')) return;
    drag.current = { sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y };
    stageRef.current.setPointerCapture(e.pointerId);
    stageRef.current.style.cursor = 'grabbing';
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    // Capturar el drag en locales: el updater de setView puede ejecutarse
    // (batching/replay del reducer) después de que onPointerUp ponga
    // drag.current = null, lo que crasheaba al leer drag.current.vx.
    const nx = d.vx + (e.clientX - d.sx);
    const ny = d.vy + (e.clientY - d.sy);
    setView((v) => ({ ...v, x: nx, y: ny }));
  };
  const onPointerUp = () => {
    drag.current = null;
    if (stageRef.current) stageRef.current.style.cursor = 'grab';
  };

  function edgePath(a, b) {
    const x1 = a.x + NODE_W;
    const y1 = a.y + NODE_H / 2;
    const x2 = b.x;
    const y2 = b.y + NODE_H / 2;
    if (connector === 'escalon') {
      const mx = x1 + 18;
      return `M${x1},${y1} H${mx} V${y2} H${x2}`;
    }
    const mx = (x1 + x2) / 2;
    return `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;
  }

  return (
    <div
      className="cmp-mapstage"
      ref={stageRef}
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      <div
        className="cmp-maplayer"
        style={{ transform: `translate(${view.x}px,${view.y}px) scale(${view.k})` }}
      >
        <svg
          className="cmp-mapedges"
          style={{
            width: bounds.minX + bounds.w + NODE_W,
            height: bounds.minY + bounds.h + NODE_H,
          }}
        >
          {edges.map((e) => {
            const a = pos[e.from];
            const b = pos[e.to];
            if (!a || !b) return null;
            const active = selectedId === e.from || selectedId === e.to;
            return (
              <path
                key={`${e.from}-${e.to}`}
                d={edgePath(a, b)}
                fill="none"
                stroke={active ? '#0055b3' : '#bcc4cd'}
                strokeWidth={active ? 2.4 : 1.6}
              />
            );
          })}
        </svg>
        {Object.values(pos).map((p) => {
          const t = typeStyle(p.node.node_type);
          const Icon = t.Icon;
          const sel = selectedId === p.node.id;
          const nodeHealth = healthMap[p.node.id];
          const dotStyle = nodeHealth ? healthStyle(nodeHealth.rolled) : deriveEstado(p.node);
          return (
            <div
              key={p.node.id}
              className={'cmp-mnode' + (sel ? ' sel' : '')}
              style={{ left: p.x, top: p.y, width: NODE_W, height: NODE_H }}
              onClick={() => onSelect(p.node)}
            >
              <span className="cmp-mn-ic" style={{ background: t.tint, color: t.color }}>
                <Icon style={{ fontSize: 16 }} />
              </span>
              <span className="cmp-mn-tx">
                <span className="cmp-mn-name">{p.node.name}</span>
                <span className="cmp-mn-type">{t.label}</span>
              </span>
              {dotStyle && (
                <span className="cmp-mn-status" style={{ background: STATUS_COLOR[dotStyle.cls] }} />
              )}
              {p.hasKids && (
                <button
                  type="button"
                  className="cmp-mn-toggle"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setOpenMap((m) => ({ ...m, [p.node.id]: !isOpen(p.node.id) }));
                  }}
                >
                  {p.open ? '−' : p.node.children ? p.node.children.length : '+'}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div className="cmp-mapctrls">
        <button type="button" onClick={() => zoomBtn(1)} title="Acercar">
          <ZoomInOutlined />
        </button>
        <button type="button" onClick={() => zoomBtn(-1)} title="Alejar">
          <ZoomOutOutlined />
        </button>
        <button type="button" onClick={fit} title="Ajustar">
          <ExpandAltOutlined />
        </button>
        <span className="cmp-zlabel">{Math.round(view.k * 100)}%</span>
      </div>
    </div>
  );
}

export default ComponentMapView;
